import type { OperationsApartmentTurnover, OperationsDayResponse } from '@/shared/api';
import {
  cleaningTaskPresentation,
  orderedCleaningTasks,
  stayPhasePresentation,
} from './operationsPresentation';

export type HeuteFilter = 'all' | 'Departure' | 'InHouse' | 'Arrival';

export interface LaneSegment {
  type: 'out' | 'clean' | 'in';
  label: string;
  /** Lage auf der Tagesachse in Prozent. */
  left: number;
  width: number;
}

export interface HeuteLane {
  apartment: OperationsApartmentTurnover;
  kind: 'Wechsel' | 'Abreise' | 'Anreise' | 'Vorgemerkte Anreise' | 'Reinigung';
  segments: LaneSegment[];
}

export interface HeuteDay {
  lanes: HeuteLane[];
  staying: Array<{ apartment: OperationsApartmentTurnover; guest: string; until: string }>;
  /** Aufenthalte in anderen Phasen (ausstehend, abgeschlossen, Daten prüfen …), mit ihrem Label. */
  others: Array<{ apartment: OperationsApartmentTurnover; guest: string; phase: string }>;
  /** Apartments ohne Aufenthalt und ohne Reinigung an diesem Tag. */
  quiet: OperationsApartmentTurnover[];
}

/** Tagesachse 06:00–22:00: genug für Abreise, Reinigungsfenster und Anreise. */
export const DAY_START = 6 * 60;
export const DAY_END = 22 * 60;
export const AXIS_HOURS = [6, 9, 12, 15, 18, 21];

export function minutesOf(time: string): number {
  const [h = '0', m = '0'] = time.split(':');
  return Number(h) * 60 + Number(m);
}

export function axisPercent(minutes: number): number {
  const ratio = (minutes - DAY_START) / (DAY_END - DAY_START);
  return Math.min(100, Math.max(0, ratio * 100));
}

function segment(type: LaneSegment['type'], label: string, from: number, to: number): LaneSegment {
  const left = axisPercent(from);
  return { type, label, left, width: Math.max(4, axisPercent(Math.max(to, from)) - left) };
}

function cleaningSegments(apartment: OperationsApartmentTurnover, date: string): LaneSegment[] {
  return orderedCleaningTasks(apartment).map((task) => {
    const from =
      task.scheduledFromLocalDate < date ? DAY_START : minutesOf(task.scheduledFromLocalTime);
    const ends = task.scheduledToLocalDate ?? task.scheduledFromLocalDate;
    // Ein Fenster ohne Ende zeichnet die Bahn zwei Stunden breit; die Tagesdetails sagen „offen“.
    const to =
      ends > date
        ? DAY_END
        : task.scheduledToLocalTime
          ? minutesOf(task.scheduledToLocalTime)
          : from + 120;
    return segment('clean', cleaningTaskPresentation(task).label, from, to);
  });
}

/** Eine vorgemerkte Anreise des Tages zählt für den Filter als Anreise. */
function matchesFilter(
  apartment: OperationsApartmentTurnover,
  filter: HeuteFilter,
  date: string,
): boolean {
  if (filter === 'all') return true;
  return apartment.stays.some(
    (stay) =>
      stay.stayPhase === filter ||
      (filter === 'Arrival' && stay.stayPhase === 'Pending' && stay.checkInDate === date),
  );
}

/** Zähler der Filter: Apartments mit mindestens einem Aufenthalt in der Phase. */
export function heuteFilterCounts(data: OperationsDayResponse): Record<HeuteFilter, number> {
  const count = (filter: HeuteFilter) =>
    data.apartments.filter((a) => matchesFilter(a, filter, data.date)).length;
  return {
    all: data.apartments.length,
    Departure: count('Departure'),
    InHouse: count('InHouse'),
    Arrival: count('Arrival'),
  };
}

/**
 * Bahnen für Wechsel, Abreisen, Anreisen (auch vorgemerkte, noch nicht bezahlte) und Reinigungen;
 * dazu wer bleibt, Aufenthalte in anderen Phasen und welche Apartments ganz ruhig sind.
 */
export function heuteDay(data: OperationsDayResponse, filter: HeuteFilter = 'all'): HeuteDay {
  const day: HeuteDay = { lanes: [], staying: [], others: [], quiet: [] };
  const checkOut = minutesOf(data.checkOutTime);
  const checkIn = minutesOf(data.checkInTime);
  for (const apartment of data.apartments) {
    if (!matchesFilter(apartment, filter, data.date)) continue;
    const departure = apartment.stays.find((stay) => stay.stayPhase === 'Departure');
    const arrival =
      apartment.stays.find((stay) => stay.stayPhase === 'Arrival') ??
      apartment.stays.find(
        (stay) => stay.stayPhase === 'Pending' && stay.checkInDate === data.date,
      );
    const pending = arrival?.stayPhase === 'Pending';
    const inHouse = apartment.stays.find((stay) => stay.stayPhase === 'InHouse');
    for (const stay of apartment.stays) {
      if (stay === departure || stay === arrival || stay === inHouse) continue;
      const phase = stayPhasePresentation(stay.stayPhase).label;
      day.others.push({ apartment, guest: stay.guestName ?? stay.reference ?? 'Gast', phase });
    }
    const cleanings = cleaningSegments(apartment, data.date);
    if (departure || arrival || cleanings.length) {
      const arrivalLabel = arrival?.guestName ?? (pending ? 'Vorgemerkt' : 'Anreise');
      const segments = [
        ...(departure
          ? [segment('out', departure.guestName ?? 'Abreise', DAY_START, checkOut)]
          : []),
        ...cleanings,
        ...(arrival
          ? [
              segment(
                'in',
                pending ? `Vorgemerkt: ${arrivalLabel}` : arrivalLabel,
                checkIn,
                DAY_END,
              ),
            ]
          : []),
      ];
      const kind =
        departure && arrival
          ? 'Wechsel'
          : departure
            ? 'Abreise'
            : arrival
              ? pending
                ? 'Vorgemerkte Anreise'
                : 'Anreise'
              : 'Reinigung';
      day.lanes.push({ apartment, kind, segments });
    } else if (inHouse) {
      day.staying.push({
        apartment,
        guest: inHouse.guestName ?? 'Gast',
        until: inHouse.checkOutDate,
      });
    } else if (apartment.stays.length === 0) {
      day.quiet.push(apartment);
    }
  }
  return day;
}
